import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import type { Shift } from '@/types'

dayjs.extend(utc)

type DepartmentShift = Pick<Shift, 'department'>
type PlannedShift = Pick<Shift, 'plan_work_start_at' | 'plan_work_end_at'>
type NamedDepartment = { id: string, name: string }

export const OTHER_DEPARTMENT_FALLBACK_NAME = 'Другое подразделение'
export const PLAN_TIME_NOT_SET = 'Время не указано'

// Смена другого подразделения: после RD-175 график отдаёт в shifts[] строки и смены
// других трудоустройств сотрудника. Они только для отображения и не участвуют
// в операциях и итогах выбранного подразделения.
export const isOtherDepartmentShift = (shift: DepartmentShift, departmentId?: string | null) => (
    Boolean(departmentId) && shift.department !== departmentId
)

// Смены выбранного подразделения. Без выбранного подразделения список не фильтруется.
export const ownDepartmentShifts = <T extends DepartmentShift>(shifts: T[], departmentId?: string | null) => (
    departmentId ? shifts.filter(shift => shift.department === departmentId) : shifts
)

// Плановый интервал «ЧЧ:ММ–ЧЧ:ММ» в формате ячейки графика; окончание на следующий день — «+1».
export const formatPlanInterval = (shift: PlannedShift) => {
    if (!shift.plan_work_start_at || !shift.plan_work_end_at) return PLAN_TIME_NOT_SET

    const start = dayjs.utc(shift.plan_work_start_at)
    const end = dayjs.utc(shift.plan_work_end_at)
    if (!start.isValid() || !end.isValid()) return PLAN_TIME_NOT_SET

    const dayShift = end.startOf('day').diff(start.startOf('day'), 'day')
    return `${start.format('HH:mm')}–${end.format('HH:mm')}${dayShift > 0 ? `+${dayShift}` : ''}`
}

export const formatOtherDepartmentShift = (
    shift: DepartmentShift & PlannedShift,
    departments: NamedDepartment[],
) => {
    const name = departments.find(item => item.id === shift.department)?.name || OTHER_DEPARTMENT_FALLBACK_NAME
    return `Смена в ${name} · ${formatPlanInterval(shift)}`
}

// Порядок подсказки не зависит от порядка shifts[] в ответе: по плановому началу, без времени — в конце.
export const sortByPlanStart = <T extends PlannedShift & { id?: string }>(shifts: T[]) => [...shifts].sort((a, b) => {
    const aStart = a.plan_work_start_at ? dayjs.utc(a.plan_work_start_at).valueOf() : Number.POSITIVE_INFINITY
    const bStart = b.plan_work_start_at ? dayjs.utc(b.plan_work_start_at).valueOf() : Number.POSITIVE_INFINITY
    if (aStart !== bStart) return aStart - bStart
    return (a.id ?? '').localeCompare(b.id ?? '')
})
